import React, { useState } from 'react';
import { useParams } from 'react-router';

import { HistoriesPanel } from '@admin/components/histories/panel';
import { NotesPanel } from '@admin/components/notes/panel';
import { Spinner } from '@admin/components/spinner';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import { History__Subject, NoteNotableEnum } from '@admin/schema';
import { LoadTypeEnum, useLogisticsLoadQuery } from '@admin/schema';

import { LogisticsLoadBasicInfoPanel } from './detail_panels/basic_info_panel';
import { LogisticsLoadCargoDetailsPanel } from './detail_panels/cargo_details_panel';
import { LogisticsLoadDocumentsPanel } from './detail_panels/documents_panel';
import { LogisticsLoadImagesPanel } from './detail_panels/images_panel';
import { LogisticsLoadInventoryPanel } from './detail_panels/inventory_panel';

export const LogisticsDetailedLoad: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data, loading, refetch } = useLogisticsLoadQuery({ client, variables: { id } });
  useTitle(data ? `Load: #${data?.load.number}` : undefined);

  const [refetching, setRefetching] = useState(false);
  const handleRefetch = async () => {
    setRefetching(true);
    try {
      await refetch();
    } finally {
      setRefetching(false);
    }
  };

  if (loading || !data) {
    return <Spinner />;
  }
  const load = data.load;

  return (
    <>
      {load.requiresAgentReview && (
        <div className="alert alert-warning" role="alert">
          <strong>Review Required:</strong> The customer has submitted a load request that has not yet received a
          response. Review the load and update it, if needed. Mark the load as reviewed, when you are ready to notify
          the customer.
        </div>
      )}

      <div className="row">
        <div className="col-md-6">
          <LogisticsLoadBasicInfoPanel load={load} />
        </div>
        <div className="col-md-6">
          <LogisticsLoadCargoDetailsPanel load={load} />
          {load.type === LoadTypeEnum.Outbound && (
            <LogisticsLoadDocumentsPanel load={load} refetching={refetching} refetch={handleRefetch} />
          )}
        </div>
      </div>
      <LogisticsLoadInventoryPanel load={load} />
      <LogisticsLoadImagesPanel load={load} />
      <NotesPanel type={NoteNotableEnum.LogisticsLoad} id={id} />
      <HistoriesPanel type={History__Subject.LogisticsLoad} id={id} timezone={load.warehouse.tz} />
    </>
  );
};
